import { h } from 'preact';
import { useState } from 'preact/hooks';
import ChevronDown from '../utils/icon/chevron-down.svg';

const ProductAccordion = ({ data }) => {
  // Initialize accordions based on open_by_default property
  const [openIndexes, setOpenIndexes] = useState(
    data.accordions.reduce((acc, item, index) => {
      if (item.open_by_default) {
        acc.push(index);
      }
      return acc;
    }, []),
  );

  const toggleAccordion = (index) => {
    setOpenIndexes((prevIndexes) =>
      prevIndexes.includes(index) ? prevIndexes.filter((i) => i !== index) : [...prevIndexes, index],
    );
  };

  const createModal = (item) => {
    const modal = document.createElement('div');
    modal.className =
      'accordion-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="accordion-modal-content bg-white p-6 max-w-lg w-full mx-4 max-h-[76vh] overflow-y-auto">
        <div class="flex justify-end items-center">
          <button class="text-gray-400 hover:text-gray-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg">
              <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19
              17.6L17.6 19L12 13.4L6.4 19Z" fill="#1D1B20"/>
            </svg>

          </button>
        </div>
        <div class="accordion-modal-content">
          ${item.modal_copy.children
            .map((child) =>
              child.children
                .map((childNode) => {
                  if (childNode.type === 'text') {
                    let content = childNode.value;
                    if (childNode.bold) content = `<span class="font-medium">${content}</span>`;
                    if (childNode.italic) content = `<em>${content}</em>`;
                    return content;
                  }
                  if (childNode.type === 'link') {
                    const target = childNode.target === '_blank' ? ' target="_blank" rel="noopener noreferrer"' : '';
                    // Get link text from children array, matching the pattern used elsewhere in the component
                    const linkText = childNode.children?.[0] ? (() => {
                      const textNode = childNode.children[0];
                      let content = textNode.value || '';
                      if (textNode.bold) content = `<span class="font-medium">${content}</span>`;
                      if (textNode.italic) content = `<em>${content}</em>`;
                      return content;
                    })() : '';
                    return `<a href="${childNode.url}" class="underline"${target}>${linkText}</a>`;
                  }
                  return '';
                })
                .join(''),
            )
            .map((content) => `<p class="text-[16px] leading-[24px] mt-2">${content}</p>`)
            .join('')}
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';

    // Function to remove the modal
    const removeModal = () => {
      modal.remove();
      // Remove the event listener when the modal is closed
      document.removeEventListener('keydown', handleEscapeKey);
      // Restore scrolling when modal is closed
      document.body.style.overflow = '';
    };

    // Event handler for the Escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        removeModal();
      }
    };

    // Add click event listener to the modal overlay
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        removeModal();
      }
    });

    // Existing close button event listener
    modal.querySelector('button').onclick = removeModal;

    // Add event listener for the Escape key
    document.addEventListener('keydown', handleEscapeKey);
  };

  const renderTextWithFormatting = (textNode) => {
    const content = textNode.value;
    let className = '';

    if (textNode.bold && textNode.italic) {
      className = 'font-medium italic';
    } else if (textNode.bold) {
      className = 'font-medium';
    } else if (textNode.italic) {
      className = 'italic';
    }

    return className ? <span className={className}>{content}</span> : content;
  };

  return (
    <div className="pdp-accordion mb-[16px]" id="product-accordion">
      {data.accordions
        .filter((item) => item.copy && item.title)
        .map((item, index) => {

          // if the accordion is for "ingredients" and the productIcons metafield is an empty array then don't render that accordion block
          if (item.title.toLowerCase().includes('ingredients') && data.productIcons.length < 1) {
            return null;
          }

          return <div key={index} className={`border-t border-alt-black ${index === data.length - 1 ? 'border-b' : ''}`}>
            <button
              className="flex justify-between items-center w-full py-4 text-left mb-0"
              onClick={(e) => {
                e.preventDefault();
                toggleAccordion(index);
              }}
            >
              <span className="text-body-2 font-medium block leading-[20px]">{item.title}</span>
              <ChevronDown
                className={`transition-transform duration-50 ${
                  openIndexes.includes(index) ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            {openIndexes.includes(index) && (
              <div className="pb-4">
                {item.title.toLowerCase().includes('ingredients') && data.productIcons && (
                  <div className="w-full pb-5 grid grid-cols-1 gap-2.5 md:gap-8 md:grid-cols-3">
                    {data.productIcons.map((icon, iconIndex) => {
                      const { title, shortDescription, image } = icon;
                      const id = title.toLowerCase().replace(' ', '-').concat(`-${iconIndex}`);

                      return (
                        <div className="flex items-center gap-3 md:gap-0 md:flex-col md:justify-start" key={id}>
                          {image && (
                            <div
                              className="min-w-[56px] w-14 h-14 [&>img]:w-14 [&>img]:h-14 [&>img]:object-cover"
                              dangerouslySetInnerHTML={{ __html: image }}
                            />
                          )}
                          <div className="flex flex-col justify-center md:items-center">
                            {title && <p className="text-headline-tag md:text-center md:pt-3">{title}</p>}
                            {shortDescription && <p className="md:text-center text-body-light-2">{shortDescription}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {item.copy.children.map((child, childIndex) => {
                  if (child.type === 'list') {
                    const ListComponent = child.listType === 'ordered' ? 'ol' : 'ul';
                    const listItemClass = child.listType === 'ordered' ? 'list-decimal' : 'list-disc';
                    return (
                      <ListComponent
                        key={childIndex}
                        className={`pl-4 list-outside ${childIndex === 0 ? 'mt-0' : 'mt-2'}`}
                      >
                        {child.children.map((listItem, listItemIndex) => (
                          <li className={`${listItemClass} pl-4 mb-3`} key={listItemIndex}>
                            {listItem.children.map((textNode, textNodeIndex) => {
                              if (textNode.type === 'link') {
                                return (
                                  <a
                                    className="underline"
                                    key={textNodeIndex}
                                    href={textNode.url}
                                    target={textNode.target === '_blank' ? '_blank' : undefined}
                                    rel={textNode.target === '_blank' ? 'noopener noreferrer' : undefined}
                                  >
                                    {renderTextWithFormatting(textNode.children[0])}
                                  </a>
                                );
                              }
                              return renderTextWithFormatting(textNode);
                            })}
                          </li>
                        ))}
                      </ListComponent>
                    );
                  }

                  if (child.type === 'paragraph') {
                    return (
                      <p key={childIndex} className={`text-black ${childIndex === 0 ? 'mt-0' : 'mt-2'}`}>
                        {child.children.map((textNode, textNodeIndex) => {
                          if (textNode.type === 'link') {
                            return (
                              <a
                                className="underline text-black"
                                key={textNodeIndex}
                                href={textNode.url}
                                target={textNode.target === '_blank' ? '_blank' : undefined}
                                rel={textNode.target === '_blank' ? 'noopener noreferrer' : undefined}
                              >
                                {renderTextWithFormatting(textNode.children[0])}
                              </a>
                            );
                          }
                          return renderTextWithFormatting(textNode);
                        })}
                      </p>
                    );
                  }

                  if (child.type === 'heading') {
                    const HeadingTag = `h${child.level}`;
                    return (
                      <HeadingTag key={childIndex} className="mt-2">
                        {child.children.map((textNode, textNodeIndex) => {
                          if (textNode.type === 'link') {
                            return (
                              <a
                                className="underline"
                                key={textNodeIndex}
                                href={textNode.url}
                                target={textNode.target === '_blank' ? '_blank' : undefined}
                                rel={textNode.target === '_blank' ? 'noopener noreferrer' : undefined}
                              >
                                {renderTextWithFormatting(textNode.children[0])}
                              </a>
                            );
                          }
                          return renderTextWithFormatting(textNode);
                        })}
                      </HeadingTag>
                    );
                  }

                  return null;
                })}
                {item.modal_cta_text && item.modal_copy && (
                  <button
                    className="accordion-modal-button mt-4 text-blue-600 underline"
                    onClick={(e) => {
                      e.preventDefault();
                      createModal(item);
                    }}
                  >
                    {item.modal_cta_text}
                  </button>
                )}
              </div>
            )}
          </div>
        })}
    </div>
  );
};

export default ProductAccordion;
